<div class="utilities-container">
  <div class="page-title">Admin Utilities</div>

  <!-- Overlay for processing state -->
  <div *ngIf="isProcessing" class="processing-overlay">
    <div class="processing-spinner">
      <mat-spinner diameter="60"></mat-spinner>
      <p class="processing-message">{{ processingTask }}...</p>
      <ng-container *ngIf="emailSessionId && emailTotal > 0; else genericNote">
        <p class="processing-progress">{{ emailSent + emailFailed }} of {{ emailTotal }} processed</p>
        <p class="processing-detail" *ngIf="emailFailed > 0">{{ emailSent }} sent &middot; {{ emailFailed }} skipped</p>
        <p class="processing-remaining">{{ emailRemaining }} remaining</p>
        <p class="processing-note">Please leave this page open to monitor progress.</p>
      </ng-container>
      <ng-template #genericNote>
        <p class="processing-note">This may take several minutes. Please wait.</p>
      </ng-template>
    </div>
  </div>

  <!-- Alerts Section -->
  <div class="utility-section">
    <div class="section-header">
      <div class="section-title">
        <h2>Alerts</h2>
        <div class="test-mode-container" *ngIf="alertsTestMode">
          <div class="test-mode-badge test-mode-active">
            <mat-icon class="test-mode-icon">warning</mat-icon>
            <span class="test-mode-text">TEST MODE</span>
          </div>
          <div class="test-mode-detail" *ngIf="testEmailSendToAddress">
            <mat-icon class="test-mode-detail-icon">email</mat-icon>
            <span>Emails to: <strong>{{ testEmailSendToAddress }}</strong></span>
          </div>
        </div>
      </div>
    </div>
    <div class="utility-description">
      <p>Run the scheduled alert batch process to send alerts to clients with details of their generated notifications</p>
    </div>
    <div class="button-container">
      <button 
        mat-raised-button 
        color="primary" 
        (click)="previewAlerts()"
        [disabled]="isProcessing || loadingAlertsPreview"
        class="utility-button preview-button">
        <mat-icon>preview</mat-icon>
        Preview Alerts
      </button>
      <button 
        mat-raised-button 
        color="primary" 
        (click)="runNotifications()"
        [disabled]="isProcessing"
        class="utility-button">
        <mat-icon>notifications</mat-icon>
        Run Alerts
      </button>
    </div>

    <!-- Alerts Preview Accordion -->
    <mat-accordion *ngIf="showAlertsPreview" class="preview-accordion">
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>info</mat-icon>
            Alerts Preview ({{ alertsPreviewData.length }} records)
          </mat-panel-title>
        </mat-expansion-panel-header>
        
        <div *ngIf="loadingAlertsPreview" class="loading-message">
          <mat-spinner diameter="30"></mat-spinner>
          Loading preview...
        </div>

        <div *ngIf="!loadingAlertsPreview && alertsPreviewData.length > 0">
          <table mat-table [dataSource]="alertsPreviewData" class="preview-table">
            <!-- Client Name Column -->
            <ng-container matColumnDef="clientName">
              <th mat-header-cell *matHeaderCellDef>Client</th>
              <td mat-cell *matCellDef="let element">{{ element.clientName }}</td>
            </ng-container>

            <!-- Subscription Name Column -->
            <ng-container matColumnDef="subscriptionName">
              <th mat-header-cell *matHeaderCellDef>Subscription</th>
              <td mat-cell *matCellDef="let element">{{ element.subscriptionName }}</td>
            </ng-container>

            <!-- Contact Name Column -->
            <ng-container matColumnDef="contactName">
              <th mat-header-cell *matHeaderCellDef>Contact</th>
              <td mat-cell *matCellDef="let element">{{ element.contactName || '-' }}</td>
            </ng-container>

            <!-- Email Column -->
            <ng-container matColumnDef="email">
              <th mat-header-cell *matHeaderCellDef>Email</th>
              <td mat-cell *matCellDef="let element">{{ element.email || '-' }}</td>
            </ng-container>

            <!-- Notification Count Column -->
            <ng-container matColumnDef="notificationCountInAlert">
              <th mat-header-cell *matHeaderCellDef>Notifications</th>
              <td mat-cell *matCellDef="let element">{{ element.notificationCountInAlert }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="alertsColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: alertsColumns;"></tr>
          </table>
        </div>

        <div *ngIf="!loadingAlertsPreview && alertsPreviewData.length === 0" class="no-data-message">
          No alerts to preview
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>

  <!-- Invoices Section -->
  <div class="utility-section">
    <div class="section-header">
      <div class="section-title">
        <h2>Invoices</h2>
        <div class="test-mode-container" *ngIf="invoicesTestMode">
          <div class="test-mode-badge test-mode-active">
            <mat-icon class="test-mode-icon">warning</mat-icon>
            <span class="test-mode-text">TEST MODE</span>
          </div>
          <div class="test-mode-detail" *ngIf="testFinanceContactId">
            <mat-icon class="test-mode-detail-icon">badge</mat-icon>
            <span>Contact ID: <strong>{{ testFinanceContactId }}</strong></span>
          </div>
        </div>
      </div>
    </div>
    <div class="utility-description">
      <p>Run the scheduled invoice batch process to process and generate invoices.</p>
    </div>
    
    <!-- Service Type Dropdown -->
    <mat-form-field class="dropdown-field">
      <mat-label>Service Type *</mat-label>
      <mat-select [(ngModel)]="selectedServiceTypeId">
        <mat-option [value]="null">-- Select Service Type --</mat-option>
        <mat-option *ngFor="let serviceType of serviceTypes" [value]="serviceType.value">
          {{ serviceType.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <!-- Frequency Dropdown -->
    <mat-form-field class="dropdown-field">
      <mat-label>Frequency *</mat-label>
      <mat-select [(ngModel)]="selectedFrequency">
        <mat-option [value]="null">-- Select Frequency --</mat-option>
        <mat-option *ngFor="let frequency of frequencies" [value]="frequency.value">
          {{ frequency.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <div class="button-container">
      <button 
        mat-raised-button 
        color="warn" 
        (click)="previewInvoices()"
        [disabled]="isProcessing || loadingInvoicesPreview || selectedServiceTypeId === null || selectedFrequency === null"
        class="utility-button preview-button">
        <mat-icon>preview</mat-icon>
        Preview Invoices
      </button>

      <button 
        mat-raised-button 
        color="accent" 
        (click)="runInvoices()"
        [disabled]="isProcessing || selectedServiceTypeId === null || selectedFrequency === null"
        class="utility-button">
        <mat-icon>receipt</mat-icon>
        Run Invoices
      </button>
    </div>

    <!-- Invoices Preview Accordion -->
    <mat-accordion *ngIf="showInvoicesPreview" class="preview-accordion">
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>info</mat-icon>
            Invoices Preview ({{ invoicesPreviewData.length }} records)
          </mat-panel-title>
        </mat-expansion-panel-header>
        
        <div *ngIf="loadingInvoicesPreview" class="loading-message">
          <mat-spinner diameter="30"></mat-spinner>
          Loading preview...
        </div>

        <div *ngIf="!loadingInvoicesPreview && invoicesPreviewData.length > 0">
          <table mat-table [dataSource]="invoicesPreviewData" class="preview-table">
            <!-- Client Name Column -->
            <ng-container matColumnDef="clientName">
              <th mat-header-cell *matHeaderCellDef>Client</th>
              <td mat-cell *matCellDef="let element">{{ element.clientName }}</td>
            </ng-container>

            <!-- Subscription Name Column -->
            <ng-container matColumnDef="subscriptionName">
              <th mat-header-cell *matHeaderCellDef>Subscription</th>
              <td mat-cell *matCellDef="let element">{{ element.subscriptionName }}</td>
            </ng-container>

            <!-- Purchase Order Reference Column -->
            <ng-container matColumnDef="purchaseOrderReference">
              <th mat-header-cell *matHeaderCellDef>PO Reference</th>
              <td mat-cell *matCellDef="let element">{{ element.purchaseOrderReference || '-' }}</td>
            </ng-container>

            <!-- Notification Count Column -->
            <ng-container matColumnDef="notificationCount">
              <th mat-header-cell *matHeaderCellDef>Notifications</th>
              <td mat-cell *matCellDef="let element">{{ element.notificationCount }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="invoicesColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: invoicesColumns;"></tr>
          </table>
        </div>

        <div *ngIf="!loadingInvoicesPreview && invoicesPreviewData.length === 0" class="no-data-message">
          No invoices to preview
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>
</div>
